import { isAxiosError } from 'axios';
import { apiClient, ApiResponse } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

// 처방전 관련 타입 정의

/**
 * 처방 확정 제외 사유. 값이 있으면 제외 상태 (null = 미제외).
 * 검수 상태(adminCheckStatus)와 독립된 축이라 수정·확정으로 덮이지 않는다.
 */
export type PrescriptionImageExclusionReason =
  | 'DUPLICATE_IMAGE'
  | 'INVALID_IMAGE';

/**
 * 목록 필터 값. EXCLUDED 는 제외 이미지만, 나머지 검수 상태는 미제외 이미지만 거른다.
 */
export type PrescriptionAdminCheckStatusFilter =
  | 'MODIFIED'
  | 'VERIFIED'
  | 'UNVERIFIED'
  | 'REJECTED'
  | 'EXCLUDED';

export interface PrescriptionSearchRequest {
  settlementYearMonth?: string;
  userEmail?: string;
  clinicName?: string;
  /** 업체명(user_profile.company_name) 부분 일치 — 서버가 공백·대소문자를 무시한다 */
  companyName?: string;
  adminCheckStatus?: PrescriptionAdminCheckStatusFilter[];
}

export interface PrescriptionInfoResponse {
  id: number;
  prescriptionImageCode: string;
  ceoName: string;
  companyName: string;
  phoneNumber: string;
  clinicName: string;
  userEmail: string;
  registrationDate: string;
  prescriptionYearMonth: string;
  settlementYearMonth: string;
  adminCheckStatus: 'MODIFIED' | 'VERIFIED' | 'UNVERIFIED' | 'REJECTED';
  mrDrugCount: number;
  allDrugsExist: boolean;
  /** 목록 자동 매칭(OCR) 상태 — NOT_REQUESTED/PENDING/PROCESSING/SUCCEEDED/FAILED */
  ocrStatus?: string;
  exclusionReason?: PrescriptionImageExclusionReason | null;
  parentCompanyNames?: string | null;
  settlementCompanyNames?: string | null;
}

/** 처방 목록 응답의 자동 매칭 통계 (admin-ruda-web ocrMatchStats) */
export interface OcrMatchStats {
  total: number;
  waiting: number;
  completed: number;
}

export interface PrescriptionListResponse
  extends PageResponse<PrescriptionInfoResponse> {
  ocrMatchStats?: OcrMatchStats | null;
}

/** 처방 등록 주체 — 어드민 업로드 vs 앱 업로드 */
export type PrescriptionRegistrationSource = 'ADMIN' | 'USER';

export interface PrescriptionImageDetailResponse {
  prescriptionImageCode: string;
  prescriptionImageUrl: string;
  userId: number;
  userEmail: string;
  ceoName: string;
  userPhoneNumber: string;
  clinicId: number;
  clinicName: string;
  registrationDate: string;
  prescriptionYearMonth: string;
  prescriptionRegisteredYearMonth: string;
  settlementYearMonth: string;
  /** 등록 주체. 삭제 가능 여부 판정에 사용 */
  registrationSource?: PrescriptionRegistrationSource;
  /** 앱 노출 여부 (true = 노출) */
  exposed?: boolean;
  /** 내역 확정 여부 — adminCheckStatus와 별개 (확정 데이터 존재) */
  finalized?: boolean;
  /** 정산 정보 존재 여부 — 있으면 삭제 불가 */
  settled?: boolean;
  adminCheckStatus: string;
  ocrStatus?: string;
  ocrErrorMessage?: string;
  /** 처방 확정 제외 — 제외 상태가 아니면 네 필드 모두 null */
  exclusionReason?: PrescriptionImageExclusionReason | null;
  excludedAt?: string | null;
  excludedBy?: number | null;
  excludedByName?: string | null;
}

export interface PrescriptionDrugInfoResponse {
  prescriptionMrId: number;
  drugCode: string;
  drugName: string;
  price: number;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
  pharmaCompanyId: number;
  pharmaCompanyName: string;
  isDrugInfoExists: boolean;
  parentCompanyId?: number | null;
  parentCompanyName?: string | null;
  settlementCompanyId?: number | null;
  settlementCompanyName?: string | null;
}

/**
 * GET /admin/prescriptions/{code}/previous-drugs 응답 항목.
 * 같은 사용자·병의원의 최근 3개월(처방등록년월 기준) **확정 처방** 의약품을 drugCode 별 1건으로 묶어 준다.
 * 약가·제약사는 과거 값이 아니라 현재 이미지 처방년월의 의약품 마스터 값이다.
 * 3개월 동안 수량이 여러 개라 수량은 내려주지 않는다(추가할 때 0에서 입력).
 */
export interface PreviousPrescriptionDrugResponse {
  drugCode: string;
  /** 마스터가 없으면 이름·약가·제약사가 모두 null */
  drugName: string | null;
  price: number | null;
  pharmaCompanyId: number | null;
  pharmaCompanyName: string | null;
  isDrugInfoExists: boolean;
  /** 이 약이 확정 처방된 이미지 중 가장 최근 처방등록년월(yyyyMM) */
  lastPrescriptionRegisteredYearMonth: string;
  /** 기간 안에서 이 약이 확정 처방된 이미지 장수 */
  prescriptionCount: number;
  /**
   * 마지막 처방월(lastPrescriptionRegisteredYearMonth)의 확정 처방 원외·원내 수량 합계.
   * 그 달 이미지가 여러 장이면 합친 값이고 다른 달은 더하지 않는다. 표시용 참고값이다.
   * 원천 수량이 null 이거나 서버 배포 전이면 비어 있을 수 있다.
   */
  lastMonthOutHospitalQuantity?: number | null;
  lastMonthInHospitalQuantity?: number | null;
  /** 현재 처방 내역(저장된 행)에 이미 있는지 */
  alreadyInCurrentPrescription: boolean;
}

export interface RegisteredPrescriptionResponse {
  prescriptionImageCode: string;
  userEmail: string;
  clinicId: number;
  clinicName: string;
  prescriptionYearMonth: string;
  registrationDate: string;
  prescriptionRegisteredYearMonth: string;
  settlementYearMonth: string;
  drugCode: string;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
  s3FileKey: string;
  imageUrl: string;
}

export interface FinalizedPrescriptionSearchRequest {
  settlementYearMonth?: string;
  clinicName?: string;
  userEmail?: string;
  /** 업체명(user_profile.company_name) 부분 일치 — 서버가 공백·대소문자를 무시한다 */
  companyName?: string;
  pharmaCompanyName?: string;
  drugName?: string;
  approvalStatus?: string;
  parentCompanyId?: number;
  settlementCompanyId?: number;
}

export interface FinalizedPrescriptionResponse {
  prescriptionId: number;
  prescriptionImageCode: string;
  prescriptionYearMonth: string;
  settlementYearMonth: string;
  email: string;
  ceoName: string;
  companyName: string;
  clinicName: string;
  pharmaCompanyName: string;
  drugCode: string;
  drugName: string;
  price: number;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
  outHospitalAmount: number;
  /** RANGE | FIXED 등 */
  basicFeeType?: string | null;
  isDetailFeeApplied?: boolean | null;
  standardCommissionRate?: number | null;
  /** 레거시 테이블: 이미 % 단위로 오는 경우가 많아 *100 하지 않음 */
  platformFeeRate?: number | null;
  commissionRate: number;
  estimatedAmount: number;
  approvalStatus: string;
  parentCompanyId?: number | null;
  parentCompanyName?: string | null;
  settlementCompanyId?: number | null;
  settlementCompanyName?: string | null;
}

export interface FinalizedPrescriptionDetailResponse {
  prescriptionId: number;
  prescriptionImageCode: string;
  userEmail: string;
  companyName: string;
  phoneNumber: string;
  prescriptionYearMonth: string;
  settlementYearMonth: string;
  clinicName: string;
  /** 병의원 사업자등록번호 — 확정 처방 이미지 다운로드 파일명에 사용 */
  businessLicenseNumber?: string;
  pharmaCompanyName: string;
  drugCode: string;
  drugName: string;
  price: number;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
  commissionRate: number;
  estimatedAmount: number;
  approvalStatus: string;
  prescriptionImageUrl: string;
  prescriptionRegisteredYearMonth: string;
  parentCompanyId?: number | null;
  parentCompanyName?: string | null;
  settlementCompanyId?: number | null;
  settlementCompanyName?: string | null;
}

export interface FinalizedPharmaCompanyResponse {
  pharmaCompanyId: number;
  pharmaCompanyName: string;
}

/** GET /admin/prescriptions/finalized/export-images 응답 */
export interface FinalizedPrescriptionImageArchiveResponse {
  fileName: string;
  downloadUrl: string;
  imageCount: number;
  expiresAt: string;
}

/** GET /admin/prescriptions/finalized/export-all/excel 응답 */
export interface FinalizedPrescriptionExcelExportResponse {
  fileName: string;
  downloadUrl: string;
  expiresAt: string;
  rowCount: number;
}

export const PRESCRIPTION_IMAGE_ARCHIVE_EMPTY_CODE =
  'PRESCRIPTION_IMAGE_ARCHIVE_EMPTY';

export const PRESCRIPTION_EXPORT_EMPTY_CODE = 'PRESCRIPTION_EXPORT_EMPTY';

/** 서버가 ZIP을 만드는 동안 응답이 지연되므로 기본 10초보다 길게 잡는다 */
export const FINALIZED_IMAGE_ARCHIVE_TIMEOUT_MS = 900_000;

/** 엑셀 생성·업로드도 행 수에 비례해 걸리므로 기본 10초보다 길게 잡는다 */
export const FINALIZED_EXCEL_EXPORT_TIMEOUT_MS = 180_000;

export interface CreateDrugPrescriptionsRequest {
  prescriptionImageCode: string;
  drugCode?: string;
  outHospitalQuantity?: number;
  inHospitalQuantity?: number;
}

export interface DrugPrescriptionCreateRequest {
  prescriptionImageCode: string;
  userId: number;
  clinicId: number;
  drugCode: string;
  /** 레거시와 동일 — 미조회면 null */
  pharmaCompanyId: number | null;
  prescriptionYearMonth: string;
  prescriptionRegisteredYearMonth: string;
  settlementYearMonth: string;
  price: number;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
}

export interface UpdatePrescriptionImageRequest {
  registrationDate: string;
  prescriptionYearMonth?: string;
  /**
   * 앱 노출 여부. 생략/null이면 서버가 현재 값을 유지.
   * 확정 후 값을 바꾸면 409, 동일 값이면 통과.
   */
  exposed?: boolean | null;
}

export interface UpdatePrescriptionDrugRequest {
  drugCode?: string;
  /** Swagger UpdatePrescriptionDrugRequest.pharmaCompanyId — 없으면 null */
  pharmaCompanyId?: number | null;
  price?: number;
  outHospitalQuantity?: number;
  inHospitalQuantity?: number;
}

/** 레거시 buildDrugPayload — 0·미조회는 null */
export const toNullablePharmaCompanyId = (
  pharmaCompanyId?: number | null,
): number | null =>
  pharmaCompanyId != null && pharmaCompanyId > 0 ? pharmaCompanyId : null;

/**
 * PATCH /admin/prescriptions/drug/{prescriptionMrId} body.
 * 제약사 ID는 항상 넣고, 없으면 null이다.
 */
export const buildUpdatePrescriptionDrugRequest = (input: {
  drugCode: string;
  pharmaCompanyId?: number | null;
  price?: number;
  outHospitalQuantity?: number;
  inHospitalQuantity?: number;
}): UpdatePrescriptionDrugRequest => ({
  drugCode: input.drugCode,
  pharmaCompanyId: toNullablePharmaCompanyId(input.pharmaCompanyId),
  price: input.price ?? 0,
  outHospitalQuantity: input.outHospitalQuantity ?? 0,
  inHospitalQuantity: input.inHospitalQuantity ?? 0,
});

export interface DeletePrescriptionMrRequest {
  prescriptionImageCodes: string[];
}

export interface FinalizePrescriptionRequest {
  prescriptionImageCode: string;
}

export interface FinalizePrescriptionsRequest {
  prescriptionImageIds: number[];
}

export interface FinalizeBySettlementMonthRequest {
  settlementYearMonth: string;
}

export interface DuplicateFinalizedPrescriptionDrug {
  mrId?: number;
  drugCode: string;
  drugName?: string;
  pharmaCompanyName?: string;
  prescriptionImageCode: string;
  inHospitalQuantity?: number;
  outHospitalQuantity?: number;
  pharmaCompanyId?: number;
  price?: number;
}

export interface PrescriptionPresignedUrlInfo {
  presignedUrl: string;
  fileKey?: string;
  filename?: string;
  [key: string]: unknown;
}

export interface PrescriptionUploadCompleteRequest {
  userId: number;
  clinicId: number;
  prescriptionYm: string;
  registrationDate: string;
  images: PrescriptionPresignedUrlInfo[];
}

export type FinalizedExportParams = {
  settlementYearMonth: string;
  pharmaCompanyId?: number;
  parentCompanyId?: number;
  companyName?: string;
};

/**
 * 확정 처방 export 쿼리.
 * 제약사·상위업체는 유효한 ID일 때만 넣고, 생략하면 서버가 조건을 걸지 않는다.
 * 0/빈 값은 루다링크가 아니므로 보내지 않는다. 빈 문자열도 넣지 않는다(400).
 * 업체명은 목록 검색어를 그대로 넘긴다. 엑셀의 이미지명이 같은 조건의 ZIP 파일명과 맞아야 하므로
 * 엑셀·ZIP에는 항상 같은 값을 보낸다.
 */
export const buildFinalizedExportParams = (
  settlementYearMonth: string,
  pharmaCompanyId?: number | null,
  parentCompanyId?: number | null,
  companyName?: string | null,
): FinalizedExportParams => {
  const params: FinalizedExportParams = { settlementYearMonth };
  if (pharmaCompanyId != null && pharmaCompanyId > 0) {
    params.pharmaCompanyId = pharmaCompanyId;
  }
  if (parentCompanyId != null && parentCompanyId > 0) {
    params.parentCompanyId = parentCompanyId;
  }
  const trimmedCompanyName = companyName?.trim();
  if (trimmedCompanyName) {
    params.companyName = trimmedCompanyName;
  }
  return params;
};

/**
 * 조건에 맞는 이미지가 하나도 없을 때 서버가 404 + PRESCRIPTION_IMAGE_ARCHIVE_EMPTY 를 준다.
 */
export const isPrescriptionImageArchiveEmptyError = (
  error: unknown,
): boolean => {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === PRESCRIPTION_IMAGE_ARCHIVE_EMPTY_CODE;
};

/** 엑셀로 추출할 확정 처방 내역이 없을 때 서버가 내려주는 404 */
export const isPrescriptionExportEmptyError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === PRESCRIPTION_EXPORT_EMPTY_CODE;
};

// 처방전 서비스
export const prescriptionService = {
  // 처방전 목록 조회
  getPrescriptions: async (
    searchRequest: PrescriptionSearchRequest,
    pageable: Pageable,
  ): Promise<PrescriptionListResponse> => {
    const params = new URLSearchParams();
    params.append('page', String(pageable.page));
    params.append('size', String(pageable.size));

    if (searchRequest.settlementYearMonth) {
      params.append('settlementYearMonth', searchRequest.settlementYearMonth);
    }
    if (searchRequest.userEmail) {
      params.append('userEmail', searchRequest.userEmail);
    }
    if (searchRequest.clinicName) {
      params.append('clinicName', searchRequest.clinicName);
    }
    if (searchRequest.companyName) {
      params.append('companyName', searchRequest.companyName);
    }
    if (searchRequest.adminCheckStatus?.length) {
      for (const status of searchRequest.adminCheckStatus) {
        params.append('adminCheckStatus', status);
      }
    }

    const response = await apiClient.get(createApiPath.prescriptions.list(), {
      params,
    });
    return response.data;
  },

  // 처방전 이미지 상세 조회
  getPrescriptionImageDetail: async (
    prescriptionImageCode: string,
  ): Promise<PrescriptionImageDetailResponse> => {
    const response = await apiClient.get(
      createApiPath.prescriptions.detail(prescriptionImageCode),
    );
    return response.data;
  },

  // 처방 약 목록 조회
  getPrescriptionDrugs: async (
    prescriptionImageCode: string,
    refYm: string,
  ): Promise<PrescriptionDrugInfoResponse[]> => {
    const response = await apiClient.get(
      createApiPath.prescriptions.drugs(prescriptionImageCode),
      {
        params: { refYm },
      },
    );
    return response.data;
  },

  // 이전 처방 의약품 조회 — 사용자·병의원·기준월은 서버가 이미지에서 꺼낸다
  getPreviousPrescriptionDrugs: async (
    prescriptionImageCode: string,
  ): Promise<PreviousPrescriptionDrugResponse[]> => {
    const response = await apiClient.get<PreviousPrescriptionDrugResponse[]>(
      createApiPath.prescriptions.previousDrugs(prescriptionImageCode),
    );
    return response.data;
  },

  // 등록 처방전 목록 조회
  getRegisteredPrescriptions: async (
    settlementYearMonth: string,
    userEmail?: string,
    clinicName?: string,
  ): Promise<RegisteredPrescriptionResponse[]> => {
    const response = await apiClient.get<RegisteredPrescriptionResponse[]>(
      createApiPath.prescriptions.registered(),
      {
        params: { settlementYearMonth, userEmail, clinicName },
      },
    );
    return response.data;
  },

  // 최종 확정 처방전 목록 조회
  getFinalizedPrescriptions: async (
    searchRequest: FinalizedPrescriptionSearchRequest,
    pageable: Pageable,
  ): Promise<PageResponse<FinalizedPrescriptionResponse>> => {
    const params: Record<string, string | number> = {
      page: pageable.page,
      size: pageable.size,
    };

    if (searchRequest.settlementYearMonth) {
      params.settlementYearMonth = searchRequest.settlementYearMonth;
    }
    if (searchRequest.clinicName) params.clinicName = searchRequest.clinicName;
    if (searchRequest.userEmail) params.userEmail = searchRequest.userEmail;
    if (searchRequest.companyName) {
      params.companyName = searchRequest.companyName;
    }
    if (searchRequest.pharmaCompanyName) {
      params.pharmaCompanyName = searchRequest.pharmaCompanyName;
    }
    if (searchRequest.drugName) params.drugName = searchRequest.drugName;
    if (searchRequest.approvalStatus) {
      params.approvalStatus = searchRequest.approvalStatus;
    }
    if (searchRequest.parentCompanyId != null) {
      params.parentCompanyId = searchRequest.parentCompanyId;
    }
    if (searchRequest.settlementCompanyId != null) {
      params.settlementCompanyId = searchRequest.settlementCompanyId;
    }

    const response = await apiClient.get(
      createApiPath.prescriptions.finalized(),
      { params },
    );
    return response.data;
  },

  // 최종 확정 처방전 상세 조회
  getFinalizedPrescriptionDetail: async (
    prescriptionId: number,
  ): Promise<FinalizedPrescriptionDetailResponse> => {
    const response = await apiClient.get(
      createApiPath.prescriptions.finalizedDetail(prescriptionId),
    );
    return response.data;
  },

  // 확정 처방전이 있는 제약사 목록 조회
  getPharmaCompaniesWithFinalizedPrescriptions: async (
    settlementYearMonth: string,
  ): Promise<FinalizedPharmaCompanyResponse[]> => {
    const response = await apiClient.get<FinalizedPharmaCompanyResponse[]>(
      createApiPath.prescriptions.finalizedPharmaCompanies(),
      {
        params: { settlementYearMonth },
      },
    );
    return response.data;
  },

  /**
   * 확정 처방 내역 엑셀.
   * 서버가 xlsx를 만들어 S3에 올린 뒤 서명 URL만 돌려주므로 클라이언트에서 엑셀을 만들지 않는다.
   * 제약사·상위업체는 있을 때만 쿼리에 넣고, 둘 다 없으면 정산 연월 전체와 같다.
   */
  exportFinalizedPrescriptionsExcel: async (
    settlementYearMonth: string,
    pharmaCompanyId?: number,
    parentCompanyId?: number,
    companyName?: string,
  ): Promise<FinalizedPrescriptionExcelExportResponse> => {
    const response =
      await apiClient.get<FinalizedPrescriptionExcelExportResponse>(
        createApiPath.prescriptions.finalizedExportAllExcel(),
        {
          params: buildFinalizedExportParams(
            settlementYearMonth,
            pharmaCompanyId,
            parentCompanyId,
            companyName,
          ),
          timeout: FINALIZED_EXCEL_EXPORT_TIMEOUT_MS,
        },
      );
    return response.data;
  },

  /**
   * 확정 처방 이미지 ZIP.
   * 서버가 S3에 올린 뒤 서명 URL만 돌려주므로 클라이언트에서 zip을 만들지 않는다.
   */
  exportFinalizedPrescriptionImages: async (
    settlementYearMonth: string,
    pharmaCompanyId?: number,
    parentCompanyId?: number,
    companyName?: string,
  ): Promise<FinalizedPrescriptionImageArchiveResponse> => {
    const response =
      await apiClient.get<FinalizedPrescriptionImageArchiveResponse>(
        createApiPath.prescriptions.finalizedExportImages(),
        {
          params: buildFinalizedExportParams(
            settlementYearMonth,
            pharmaCompanyId,
            parentCompanyId,
            companyName,
          ),
          timeout: FINALIZED_IMAGE_ARCHIVE_TIMEOUT_MS,
        },
      );
    return response.data;
  },

  // 처방 약 목록 등록
  createDrugPrescriptions: async (
    requests: CreateDrugPrescriptionsRequest[],
  ): Promise<ApiResponse<void>> => {
    const response = await apiClient.post(
      createApiPath.prescriptions.drugsCreate(),
      requests,
    );
    return response.data;
  },

  // 처방전 약 목록 삭제
  deletePrescriptionDrugs: async (
    request: DeletePrescriptionMrRequest,
  ): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(
      createApiPath.prescriptions.drugsDelete(),
      {
        data: request,
      },
    );
    return response.data;
  },

  // 처방 약 등록
  createDrugPrescription: async (
    request: DrugPrescriptionCreateRequest,
  ): Promise<ApiResponse<void>> => {
    const response = await apiClient.post(
      createApiPath.prescriptions.drugCreate(),
      request,
    );
    return response.data;
  },

  // 처방 약 수정
  updatePrescriptionDrug: async (
    prescriptionMrId: number,
    request: UpdatePrescriptionDrugRequest,
  ): Promise<void> => {
    await apiClient.patch(
      createApiPath.prescriptions.drugUpdate(prescriptionMrId),
      request,
    );
  },

  // 처방 약 삭제
  deletePrescriptionDrug: async (
    prescriptionMrId: number,
  ): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(
      createApiPath.prescriptions.drugDelete(prescriptionMrId),
    );
    return response.data;
  },

  // 처방 이미지 수정
  updatePrescriptionImage: async (
    prescriptionImageCode: string,
    request: UpdatePrescriptionImageRequest,
  ): Promise<void> => {
    await apiClient.patch(
      createApiPath.prescriptions.imageUpdate(prescriptionImageCode),
      request,
    );
  },

  /**
   * 처방 이미지·내역 삭제 (복구 불가).
   * ADMIN 등록·미확정·미정산 건만 허용. 성공 시 204.
   */
  deletePrescriptionImage: async (
    prescriptionImageCode: string,
  ): Promise<void> => {
    await apiClient.delete(
      createApiPath.prescriptions.delete(prescriptionImageCode),
    );
  },

  finalizePrescription: async (
    request: FinalizePrescriptionRequest,
  ): Promise<void> => {
    await apiClient.post(createApiPath.prescriptions.finalize(), request);
  },

  // 처방전 일괄 확정
  finalizePrescriptions: async (
    request: FinalizePrescriptionsRequest,
  ): Promise<void> => {
    await apiClient.post(createApiPath.prescriptions.finalizeBatch(), request);
  },

  /** 정산년월 기준 모든 처방 확정 — 레거시 finalize/settlement-month */
  finalizePrescriptionsBySettlementMonth: async (
    request: FinalizeBySettlementMonthRequest,
  ): Promise<void> => {
    await apiClient.post(
      createApiPath.prescriptions.finalizeSettlementMonth(),
      request,
    );
  },

  createPresignedUrls: async (
    filenames: string[],
  ): Promise<PrescriptionPresignedUrlInfo[]> => {
    const response = await apiClient.post<PrescriptionPresignedUrlInfo[]>(
      createApiPath.prescriptions.presignedUrls(),
      { filenames },
    );
    return response.data;
  },

  registerPrescriptionUpload: async (
    request: PrescriptionUploadCompleteRequest,
  ): Promise<void> => {
    await apiClient.post(createApiPath.prescriptions.uploadComplete(), request);
  },

  cancelFinalizedPrescription: async (
    prescriptionImageCode: string,
  ): Promise<void> => {
    await apiClient.delete(
      createApiPath.prescriptions.cancelFinalized(prescriptionImageCode),
    );
  },

  findDuplicateFinalizedPrescriptionDrugs: async (
    prescriptionImageCode: string,
  ): Promise<DuplicateFinalizedPrescriptionDrug[]> => {
    const response = await apiClient.get<DuplicateFinalizedPrescriptionDrug[]>(
      createApiPath.prescriptions.finalizedDuplicates(prescriptionImageCode),
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  requestAutoMatchingRetry: async (
    prescriptionImageCode: string,
  ): Promise<{ requested: number; updated: number }> => {
    const response = await apiClient.post<{
      requested: number;
      updated: number;
    }>(createApiPath.prescriptions.ocrRetry(), {
      prescriptionImageCodes: [prescriptionImageCode],
    });
    return response.data;
  },

  /**
   * 처방 확정 제외 지정. 이미 제외된 이미지에 보내면 사유·시각·지정자를 갱신한다.
   * 확정 행·정산 정보가 있으면 409 (확정 취소 후 제외).
   */
  excludePrescriptionImage: async (
    prescriptionImageCode: string,
    reason: PrescriptionImageExclusionReason,
  ): Promise<void> => {
    await apiClient.patch(
      createApiPath.prescriptions.exclusion(prescriptionImageCode),
      { reason },
    );
  },

  /** 처방 확정 제외 해제. 제외 상태가 아니어도 204 (멱등) */
  cancelPrescriptionImageExclusion: async (
    prescriptionImageCode: string,
  ): Promise<void> => {
    await apiClient.delete(
      createApiPath.prescriptions.exclusion(prescriptionImageCode),
    );
  },

  /** 하이라이터용 저장된 OCR raw — 레거시 getPrescriptionOcrRaw (404면 null) */
  getPrescriptionOcrRaw: async (
    prescriptionImageCode: string,
  ): Promise<unknown | null> => {
    try {
      const response = await apiClient.get(
        createApiPath.prescriptions.ocrRaw(prescriptionImageCode),
      );
      return response.data ?? null;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) return null;
      throw err;
    }
  },
};
